import { ComputedRef, computed, ref, Ref } from "vue";
import { updateEmployeesAffiliate } from "@/api/user";
import { showNotification, showServerError } from "@/utils";
import { EDITABLE_FIELD, IAffiliateInfoItem } from "./types";

interface UseCardEditOptions {
    affiliateId: () => number;
    getItems: () => Record<string, IAffiliateInfoItem>;
    editableFields: () => Record<string, any>;
    getChangedValues: () => Record<string, any>;
    resetFields: (keys: EDITABLE_FIELD[]) => void;
    ownedKeys: EDITABLE_FIELD[];
    onAfterSave?: () => void;
}

const DELAYED_FIELDS: string[] = [EDITABLE_FIELD.STATUS, EDITABLE_FIELD.INVOICE_MASTER];

export function useCardEdit(options: UseCardEditOptions): {
    isEdit: Ref<boolean>;
    isLoading: Ref<boolean>;
    isDisableEdit: ComputedRef<boolean>;
    editInfo: (value: boolean) => Promise<void>;
    resetInfo: () => void;
} {
    const isEdit = ref(false);
    const isLoading = ref(false);

    const isDisableEdit = computed(() => {
        return Object.values(options.getItems()).some(
            item => 'isValid' in item && !item.isValid,
        );
    });

    const saveCard = async () => {
        const allChanged = options.getChangedValues();
        const changed: Record<string, any> = {};
        for (const key of options.ownedKeys) {
            if (key in allChanged) changed[key] = allChanged[key];
        }
        if (Object.keys(changed).length === 0) return;

        const items = options.getItems();
        const requests: Function[] = [];
        const delayed: Function[] = [];
        const otherFields: string[] = [];

        for (const key of Object.keys(changed)) {
            const item = items[key];
            if (!item) continue;

            if (item.updateMethod) {
                (DELAYED_FIELDS.includes(key) ? delayed : requests).push(item.updateMethod);
            } else {
                otherFields.push(key);
            }
        }

        const batchUpdate = async () => {
            if (otherFields.length === 0) return;
            const dto: Record<string, any> = {};
            for (const key of otherFields) {
                dto[key === EDITABLE_FIELD.NICKNAME ? 'nick_name' : key] = options.editableFields()[key];
            }
            await updateEmployeesAffiliate(options.affiliateId(), dto);
        };

        await Promise.all([...requests, batchUpdate].map(fn => fn()));
        if (delayed.length) await Promise.all(delayed.map(fn => fn()));
    };

    const editInfo = async (value: boolean) => {
        isEdit.value = value;
        if (value) return;

        try {
            isLoading.value = true;
            await saveCard();
            options.onAfterSave?.();
            showNotification('Изменения сохранены');
        } catch (err) {
            showServerError(err, 'Ошибка сохранения изменений');
            options.resetFields(options.ownedKeys);
        } finally {
            isLoading.value = false;
        }
    };

    const resetInfo = () => {
        isEdit.value = false;
        options.resetFields(options.ownedKeys);
    };

    return { isEdit, isLoading, isDisableEdit, editInfo, resetInfo };
}
